import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TRestoreTicketValidation,
  RestoreTicketValidationSchema,
  useRestoreTicketMutation,
} from '../../../api/restore-ticket.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_SINGLE_TICKET_QUERY_KEY, TGetSingleTicketResponse } from '../../../api/get-single-ticket.api';
import {
  GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY,
  TGetListArchiveTicketBySpaceResponse,
} from '../../../api/get-archive-ticket-by-space.api';
import {
  GET_LIST_TICKET_BY_SPACE_QUERY_KEY,
  TGetListTicketBySpaceResponse,
} from '../../../api/get-ticket-by-space.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { TTicketBySpace } from '../../../types/ticket.type';

export interface IRestoreTicketModalProps {
  spaceId: string;
  ticketId: string;
  onStart?: () => Promise<void> | void;
  onSuccess?: () => Promise<void> | void;
}

interface IRestoreTicketOptimisticContext {
  previousArchiveData?: TGetListArchiveTicketBySpaceResponse;
  previousActiveData?: TGetListTicketBySpaceResponse;
  previousSingleTicketData?: TGetSingleTicketResponse;
  restoredTicket?: TTicketBySpace['tickets'][0];
  restoredTicketColumnId?: string;
}

export function useRestoreTicketModal({ spaceId, ticketId, onStart, onSuccess }: IRestoreTicketModalProps) {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();

  const archiveQueryKey = [GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];
  const activeQueryKey = [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }];
  const singleTicketQueryKey = [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }];

  const mutationRestoreTicket = useRestoreTicketMutation({
    mutationConfig: {
      onMutate: async (): Promise<void> => {
        if (onStart) {
          await onStart();
        }

        // // Cancel any outgoing refetches to avoid overwriting optimistic update
        // await Promise.all([
        //   queryClient.cancelQueries({ queryKey: archiveQueryKey }),
        //   queryClient.cancelQueries({ queryKey: activeQueryKey }),
        //   queryClient.cancelQueries({ queryKey: singleTicketQueryKey }),
        // ]);

        // // Snapshot the previous values for rollback
        // const previousArchiveData = queryClient.getQueryData<TGetListArchiveTicketBySpaceResponse>(archiveQueryKey);
        // const previousActiveData = queryClient.getQueryData<TGetListTicketBySpaceResponse>(activeQueryKey);
        // const previousSingleTicketData = queryClient.getQueryData<TGetSingleTicketResponse>(singleTicketQueryKey);

        // // Find the ticket in archive data
        // let restoredTicket: TTicketBySpace['tickets'][0] | undefined;
        // let restoredTicketColumnId: string | undefined;

        // if (previousArchiveData?.payload) {
        //   for (const column of previousArchiveData.payload) {
        //     const ticket = column.tickets?.find((t) => t.ticketId === ticketId);
        //     if (ticket) {
        //       restoredTicket = ticket;
        //       restoredTicketColumnId = column.spaceColumnId;
        //       break;
        //     }
        //   }
        // }

        // // Optimistically remove ticket from archive list
        // if (previousArchiveData?.payload && restoredTicket && restoredTicketColumnId) {
        //   queryClient.setQueryData<TGetListArchiveTicketBySpaceResponse>(archiveQueryKey, (old) => {
        //     if (!old?.payload) return old;

        //     return {
        //       ...old,
        //       payload: old.payload.map((column) => {
        //         if (column.spaceColumnId === restoredTicketColumnId) {
        //           return {
        //             ...column,
        //             tickets: column.tickets?.filter((t) => t.ticketId !== ticketId) || [],
        //           };
        //         }
        //         return column;
        //       }),
        //     };
        //   });
        // }

        // // Optimistically add ticket back to active list
        // if (previousActiveData?.payload && restoredTicket && restoredTicketColumnId) {
        //   queryClient.setQueryData<TGetListTicketBySpaceResponse>(activeQueryKey, (old) => {
        //     if (!old?.payload) return old;

        //     // Find the column in active list
        //     const targetColumnIndex = old.payload.findIndex((col) => col.spaceColumnId === restoredTicketColumnId);

        //     if (targetColumnIndex >= 0) {
        //       // Column exists, add ticket to it
        //       const newPayload = [...old.payload];
        //       newPayload[targetColumnIndex] = {
        //         ...newPayload[targetColumnIndex],
        //         tickets: [...(newPayload[targetColumnIndex].tickets || []), restoredTicket],
        //       };
        //       return {
        //         ...old,
        //         payload: newPayload,
        //       };
        //     } else {
        //       // Column doesn't exist in active list, create it with the ticket
        //       return {
        //         ...old,
        //         payload: [
        //           ...old.payload,
        //           {
        //             spaceColumnId: restoredTicketColumnId,
        //             name:
        //               previousArchiveData?.payload?.find((col) => col.spaceColumnId === restoredTicketColumnId)?.name ||
        //               '',
        //             columnIndex:
        //               previousArchiveData?.payload?.find((col) => col.spaceColumnId === restoredTicketColumnId)
        //                 ?.columnIndex || '0',
        //             color:
        //               previousArchiveData?.payload?.find((col) => col.spaceColumnId === restoredTicketColumnId)
        //                 ?.color || '#000000',
        //             tickets: [restoredTicket],
        //           },
        //         ],
        //       };
        //     }
        //   });
        // }

        // // Optimistically update single ticket query if we have the ticket data
        // // Note: We can't fully update it without fetching, but we can mark it as not archived
        // // For now, we'll just invalidate it in onSettled

        // return {
        //   previousArchiveData,
        //   previousActiveData,
        //   previousSingleTicketData,
        //   restoredTicket,
        //   restoredTicketColumnId,
        // };
      },
      onError: (error: AxiosErrorCustom, _variables: TRestoreTicketValidation, context: unknown) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error restore ticket, please try again';
        showToast({ message: errorMessage, type: 'error' });

        // // Rollback to previous data on error
        // if (context && typeof context === 'object' && 'previousArchiveData' in context) {
        //   const typedContext = context as IRestoreTicketOptimisticContext;
        //   if (typedContext.previousArchiveData !== undefined) {
        //     queryClient.setQueryData(archiveQueryKey, typedContext.previousArchiveData);
        //   }
        //   if (typedContext.previousActiveData !== undefined) {
        //     queryClient.setQueryData(activeQueryKey, typedContext.previousActiveData);
        //   }
        //   if (typedContext.previousSingleTicketData !== undefined) {
        //     queryClient.setQueryData(singleTicketQueryKey, typedContext.previousSingleTicketData);
        //   }
        // }
      },
      onSuccess: async () => {
        showToast({ message: 'Ticket restored successfully!', type: 'success' });
        modalActions.closeModal('RestoreTicketModal');
        form.reset();

        // Call the onSuccess callback if provided (e.g., to update form in parent modal)
        if (onSuccess) {
          await onSuccess();
        }
      },
      onSettled: async () => {
        // Invalidate queries to ensure we have the latest data from server
        // if (spaceId && ticketId) {
        //   await Promise.all([
        //     queryClient.invalidateQueries({
        //       queryKey: [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }],
        //     }),
        //     queryClient.invalidateQueries({
        //       queryKey: [GET_TICKET_ACTIVITY_QUERY_KEY, { ticketId }],
        //     }),
        //     queryClient.invalidateQueries({
        //       queryKey: [GET_LIST_ARCHIVE_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
        //     }),
        //     queryClient.invalidateQueries({
        //       queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
        //     }),
        //   ]);
        // }
      },
    },
  });

  const form = useForm<TRestoreTicketValidation>({
    resolver: zodResolver(RestoreTicketValidationSchema),
    mode: 'onChange',
    defaultValues: {
      ticketId: '',
    },
  });

  // HELPERS
  /*
    Separate loading form and loading mutation
   */
  const isOpen = useMemo(() => {
    return modalActions.checkIsOpenModal('RestoreTicketModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationRestoreTicket.isPending || form.formState.isSubmitting;
  }, [mutationRestoreTicket, form.formState.isSubmitting]);

  const isLoadingForm = useMemo(() => {
    return mutationRestoreTicket.isPending || form.formState.isSubmitting;
  }, [mutationRestoreTicket, form.formState.isSubmitting]);

  // EFFECTS
  useEffect(() => {
    if (ticketId) {
      form.reset({
        ticketId: ticketId,
        spaceId: spaceId,
      });
    }
  }, [ticketId, spaceId, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('RestoreTicketModal');
  }, [modalActions]);

  const handleCancelModal = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  const handleSubmitForm = useCallback(
    (data: TRestoreTicketValidation) => {
      mutationRestoreTicket.mutate({
        spaceId: spaceId,
        ticketId: data?.ticketId,
      });
    },
    [mutationRestoreTicket]
  );

  return {
    // FORM
    form,
    isOpen,
    isDisabledForm,
    isLoadingForm,

    // HANDLERS
    handleCancelModal,
    handleCloseModal,
    handleSubmitForm,
  };
}
